import React, { useState, useEffect } from "react";
import { Query, Column, Action } from "material-table";
import { useSnackbar } from "notistack";
import moment from "moment";
import { DefaultTable } from "components";
import { useMounted } from "utils/hooks";
import { getMembershipRequests, respondMembershipRequests, MembershipRequest } from "api/admin/members";
import i18n from "./i18n";
import RequestsBulkEditDialog from "./RequestsBulkEditDialog";

// prop types
interface RequestsTabProps {
    clubId?: string,
}

// admin members table
const RequestsTab = ({ clubId }: RequestsTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [isLoading, setLoading] = useState(false);
    const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);

    // convert material-table query to API params
    const convertQueryToParams = (query: Query<MembershipRequest>) => {
        // paging
        const params: Parameters<typeof getMembershipRequests>[0] = {
            limit: query.pageSize,
            offset: query.pageSize * query.page,
        };

        // sorting
        if (query.orderBy) {
            params.sortOrder = query.orderDirection.toUpperCase() as typeof params["sortOrder"];
            params.sortKey = query.orderBy.field as typeof params["sortKey"];
        }

        // filter
        params.query = query.search;
        params.clubId = clubId;

        return params;
    };

    // table actions
    const tableActions: Action<MembershipRequest>[] = [
        {
            icon: "gavel",
            isFreeAction: true,
            tooltip: i18n.bulkEdit,
            onClick: () => setShowBulkEditDialog(true),
        },
        {
            icon: "check",
            tooltip: i18n.accept,
            onClick: (e, data) => respondRequests(data as MembershipRequest[], true),
        },
        {
            icon: "close",
            tooltip: i18n.decline,
            onClick: (e, data) => respondRequests(data as MembershipRequest[], false),
        },
    ];

    // table columns
    const tableColumns: Column<MembershipRequest>[] = [
        {
            field: "firstName",
            render: ({ user }) => user.firstName,
            title: i18n.firstName,
        },
        {
            field: "lastName",
            render: ({ user }) => user.lastName,
            title: i18n.lastName,
        },
        {
            field: "user.email",
            sorting: false,
            title: i18n.emailAddress,
        },
        {
            field: "club.shortName",
            sorting: false,
            title: i18n.club,
        },
        {
            field: "createdAt",
            render: ({ createdAt }) => moment(createdAt).format("DD.MM.YYYY HH:mm"),
            title: i18n.date,
        },
    ];

    // refresh (-> remote data)
    const tableRef = React.useRef<any>(null);
    const isMounted = useMounted();
    const refreshTable = () => {
        if (!isMounted) return; // skip on initial material-table fetch
        if (tableRef.current) tableRef.current.onQueryChange();
    };
    useEffect(refreshTable, [clubId]);

    // response requests
    const respondRequests = async (requests: MembershipRequest[], approve: boolean) => {
        setLoading(true);
        try {
            await respondMembershipRequests({
                approve,
                requestIds: requests.map(({ id }) => id),
            });
            refreshTable();
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // render
    return (
        <>
            {/* membership requests table */}
            <DefaultTable
                actions={tableActions}
                columns={tableColumns}
                data={(query) => new Promise((resolve) => {
                    const params = convertQueryToParams(query);
                    getMembershipRequests(params)
                        .then((response) => resolve({
                            ...response,
                            page: query.page,
                        }))
                        .catch((err) => {
                            displayError(err);
                            resolve({ data: [], page: 0, totalCount: 0 });
                        });
                })}
                isLoading={isLoading}
                options={{ selection: true }}
                tableRef={tableRef}
                title={i18n.membershipRequests}
            />

            {/* dialogs */}
            <RequestsBulkEditDialog
                showDialog={showBulkEditDialog}
                onClose={() => setShowBulkEditDialog(false)}
                onRefreshRequestsTable={refreshTable}
            />
        </>
    );
};

export default RequestsTab;
